<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import type { Sector } from '@/stores/admin'
import { slugify } from '@/lib/utils'
import { sectorCreateSchema, sectorUpdateSchema } from '@/views/forms/schema'
import type { FormSubmitEvent } from '@nuxt/ui'
import type { z } from 'zod'
import EmojiPicker from 'vue3-emoji-picker'
import 'vue3-emoji-picker/css'

const props = defineProps<{
  sector: Sector | null
}>()

const emit = defineEmits<{
  submit: [data: { name: string; slug?: string; emoji?: string }]
  cancel: []
}>()

const isEditMode = computed(() => !!props.sector)

const state = ref({ name: '', slug: '', emoji: '' })

const showEmojiPicker = ref(false)

watch(
  () => props.sector,
  (newSector) => {
    state.value = {
      name: newSector?.name || '',
      slug: newSector?.slug || '',
      emoji: newSector?.emoji || '',
    }
  },
  { immediate: true },
)

watch(
  () => state.value.name,
  (newName) => {
    if (!isEditMode.value && newName) {
      state.value.slug = slugify(newName)
    }
  },
)

const schema = computed(() => (isEditMode.value ? sectorUpdateSchema : sectorCreateSchema))

function onSubmit(
  event: FormSubmitEvent<z.output<typeof sectorCreateSchema | typeof sectorUpdateSchema>>,
) {
  const data = event.data as z.output<typeof sectorCreateSchema>
  emit('submit', {
    name: data.name,
    slug: isEditMode.value ? undefined : data.slug,
    emoji: state.value.emoji, // emoji not in Zod schema validation, empty string clears it
  })
}

function onSelectEmoji(emoji: { i: string }) {
  state.value.emoji = emoji.i
  showEmojiPicker.value = false
}

function clearEmoji() {
  state.value.emoji = ''
  showEmojiPicker.value = false
}

const isSubmitDisabled = computed(() => {
  if (!state.value.name.trim()) return true
  if (!isEditMode.value && !state.value.slug.trim()) return true
  return false
})
</script>

<template>
  <div class="p-4 space-y-6">
    <UForm :schema="schema" :state="state" class="space-y-4" @submit="onSubmit">
      <UFormField label="Name" name="name" required>
        <UInput v-model="state.name" placeholder="Sector name" class="w-full" />
      </UFormField>

      <UFormField label="Slug" name="slug" :required="!isEditMode">
        <UInput
          v-model="state.slug"
          :disabled="isEditMode"
          placeholder="sector-slug"
          class="w-full font-mono"
        />
        <template #hint v-if="isEditMode">
          <span class="text-xs text-muted">Slug cannot be changed after creation</span>
        </template>
        <template #hint v-else>
          <span class="text-xs text-muted">Auto-generated from name. You can customize it.</span>
        </template>
      </UFormField>

      <UFormField label="Emoji" name="emoji">
        <UPopover v-model:open="showEmojiPicker">
          <UButton color="neutral" variant="outline" class="w-32 justify-between">
            <span v-if="state.emoji" class="text-xl">{{ state.emoji }}</span>
            <span v-else class="text-muted">Select...</span>
            <UIcon name="i-lucide-chevron-down" class="text-muted" />
          </UButton>
          <template #content>
            <div class="relative">
              <button
                v-if="state.emoji"
                type="button"
                class="absolute top-2 right-2 z-10 p-1 rounded bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
                @click="clearEmoji"
              >
                <UIcon name="i-lucide-x" class="text-sm" />
              </button>
              <EmojiPicker :native="true" :disable-skin-tones="true" @select="onSelectEmoji" />
            </div>
          </template>
        </UPopover>
        <template #hint>
          <span class="text-xs text-muted">Optional emoji to display in the table</span>
        </template>
      </UFormField>

      <div class="flex justify-end gap-2 pt-4">
        <UButton type="button" color="neutral" variant="soft" @click="emit('cancel')">
          Cancel
        </UButton>
        <UButton type="submit" color="primary" :disabled="isSubmitDisabled">
          {{ isEditMode ? 'Update Sector' : 'Create Sector' }}
        </UButton>
      </div>
    </UForm>
  </div>
</template>
